























import { Component, Prop, Vue } from "vue-property-decorator";
import { MiaPlaza } from "@/Reinforced.Typings";
import SubjectHelper from "@/utils/SubjectHelper";
import I18Next from "@/Control/Vue/I18Next";
import Unescape from "lodash-es/unescape";
import FiltersStateSessionStorage, { Selection } from "./FiltersStateSessionStorage";
import CloneDeep from "lodash-es/cloneDeep";
import { ISubjectFilteringOptionsWithState, SubjectDropDownOptionState, SeparatorOption, ISubjectDropDownOption, ICourseDropDownOption } from "./SelectorTypes";

@Component({
	components: { 
		SubjectSelectionDropdown: async () => (await import("./SubjectSelectionDropdown.vue")),
		CourseSelectionDropdown: async () => (await import("./CourseSelectionDropdown.vue"))
	}
})
export default class SubjectAndCourseSelector extends Vue {
	@Prop({ type: Array, required: true}) protected filteringOptions!: ISubjectFilteringOptionsWithState[];
	@Prop({ type: String, required: true}) protected child!: string;

	private sortedFilteringOptions!: ISubjectFilteringOptionsWithState[];

	private subjectOptions: ISubjectDropDownOption[] = [];
	private courseOptions: ICourseDropDownOption[] = [];

	private subjectSelection?: MiaPlaza.Model.Subject[];
	private courseSelection?: string[];
		
	private async mounted() {
		const savedState = FiltersStateSessionStorage.GetFiltersData(this.child);

		if(savedState != null) {
			await this.recoverFiltersFromSession(savedState);
		}
		
		this.$watch("filteringOptions", this.onOptionsChange.bind(this), { immediate: savedState == null });
	}

	private onOptionsChange() {
		this.sortedFilteringOptions = CloneDeep(this.filteringOptions).sort(this.disableSorter.bind(this));

		this.subjectOptions = this.sortedFilteringOptions.map(option => 
			({
				label: "",
				subjectIdentifier: option.Subject,
				state: option.Disabled ? SubjectDropDownOptionState.Disabled : SubjectDropDownOptionState.Selected
			})
		);
		void this.getSubjectTitles().then(() => {
			this.subjectSelectionChanged(this.subjectOptions.filter(option => option.state === SubjectDropDownOptionState.Selected).map(option => option.subjectIdentifier));
			this.courseSelectionChanged(this.courseOptions.filter(option => option.state === SubjectDropDownOptionState.Selected).map(option => option.courseIdentifier));
		});
	}

	private async recoverFiltersFromSession(savedState: Selection): Promise<void> {
		this.sortedFilteringOptions = CloneDeep(this.filteringOptions).sort(this.disableSorter.bind(this));

		this.subjectOptions = this.sortedFilteringOptions.map(option => ({
			label: "",
			subjectIdentifier: option.Subject,
			state: option.Disabled 
				? SubjectDropDownOptionState.Disabled 
				: savedState.Subjects.includes(option.Subject) 
						? SubjectDropDownOptionState.Selected 
						: SubjectDropDownOptionState.Unselected
		}));

		this.subjectSelection = savedState.Subjects;

		await this.getSubjectTitles();

		this.courseOptions = this.createCourseOptions((subject, courseIdentifier) => {
			return savedState.Courses.includes(courseIdentifier) ? SubjectDropDownOptionState.Selected : SubjectDropDownOptionState.Unselected;
		});

		this.courseSelectionChanged(savedState.Courses);
	}

	private onSubjectInput(subjects: MiaPlaza.Model.Subject[]) {
		this.subjectSelectionChanged(subjects);
		this.onCourseInput(this.courseOptions.filter(option => option.state === SubjectDropDownOptionState.Selected).map(option => option.courseIdentifier));
	}

	private onCourseInput(courses: string[]) {
		this.courseSelectionChanged(courses);
		FiltersStateSessionStorage.SaveSubjects(this.child, this.subjectSelection ?? []);
		FiltersStateSessionStorage.SaveCourses(this.child, courses);
	}

	private async getSubjectTitles(): Promise<any> {
		const promiseStorage: Array<Promise<any>> = [];
		for (const option of this.subjectOptions) {
			promiseStorage.push(
				(async () => {
					option.label = Unescape(await I18Next.tr(SubjectHelper.GetTrKey(option.subjectIdentifier)));
				})()
			)
		}
		await Promise.all(promiseStorage);
	}

	private subjectSelectionChanged(options: MiaPlaza.Model.Subject[]) {
		const subjectsAdded = options.filter(option => !(this.subjectSelection ?? []).includes(option));
		this.subjectSelection = options;

		this.courseOptions = this.createCourseOptions((subject, courseIdentifier) => {
			if(this.courseSelection == null || subjectsAdded.includes(subject)) {
				return SubjectDropDownOptionState.Selected;
			}
			return this.courseSelection.includes(courseIdentifier) ? SubjectDropDownOptionState.Selected : SubjectDropDownOptionState.Unselected;
		});
	}

	private createCourseOptions(
			stateSelector: (subject: MiaPlaza.Model.Subject, courseIdentifier: string) => SubjectDropDownOptionState.Selected | SubjectDropDownOptionState.Unselected
		): ICourseDropDownOption[] {
		const newCourseOptions: ICourseDropDownOption[] =[];

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const subjects = this.subjectOptions.filter(subjectOption => this.subjectSelection!.includes(subjectOption.subjectIdentifier));

		for(const subject of subjects) {
			const subjectCourses = this.sortedFilteringOptions.find(option => option.Subject === subject.subjectIdentifier)?.CourseOptions ?? [];
			
			subjectCourses.sort(this.disableSorter.bind(this));

			newCourseOptions.push({
				label: subject.label,
				courseIdentifier: subject.subjectIdentifier,
				state: SeparatorOption.Separator
			});

			for(const courseOption of subjectCourses) {
				let state = courseOption.Disabled 
					? SubjectDropDownOptionState.Disabled 
					: stateSelector(subject.subjectIdentifier, courseOption.Course);

				newCourseOptions.push({
					label: courseOption.CourseName,
					courseIdentifier: courseOption.Course,
					state: state
				});
			}
		}

		return newCourseOptions;
	}

	private courseSelectionChanged(newSelection: string[]) {
		this.courseSelection = newSelection;
		this.$emit('courseSelectionChanged', newSelection);
	}

	private disableSorter(a: { Disabled: boolean }, b: { Disabled: boolean }) {
		return (a.Disabled === b.Disabled) ? 0 : (a.Disabled ? 1 : -1);
	}
}
